'use client';

import { useQuery } from '@tanstack/react-query';
import {
  useAuthStore,
  selectAuthHasHydrated,
  selectIsAuthenticated,
  selectUser,
} from '@/stores/auth-store';
import { getOrders } from '@/lib/api/orders';

/**
 * Оплаченные заказы аккаунта — ответ на вопрос «первый ли это заказ».
 *
 * GET /orders без include_unpaid отдаёт только оплаченные заказы — сервер
 * проверяет ролл за первый заказ по ним же. Ключ общий с оформлением и страницей
 * акции (там своя копия запроса с retry: false), поэтому кэш у них один.
 * staleTime по умолчанию 0: оформление перепроверяет при каждом открытии.
 */
export function useFirstOrderQuery(enabled: boolean, staleTime = 0) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore(selectUser);
  return useQuery({
    queryKey: ['checkout-first-order', user?.id, user?.phone],
    queryFn: () => getOrders(false),
    enabled: enabled && isAuthenticated,
    staleTime,
  });
}

/**
 * 'eligible' — вошёл и оплаченных заказов нет; 'guest' — не вошёл;
 * 'unknown' — ответа ещё нет или запрос не удался: тогда о подарке молчим.
 */
export type FirstOrderState = 'eligible' | 'already-ordered' | 'guest' | 'unknown';

/**
 * Помощнику нужен только флаг, а полная история заказов — тяжёлый запрос: за
 * минуту ответ не устареет, и цикл «Изменить пожелания → Подобрать» его не повторяет.
 * Оформление всё равно перепроверит со своим staleTime 0.
 */
const ASSISTANT_STALE_TIME = 60_000;

export function useFirstOrderState(enabled: boolean): FirstOrderState {
  const hydrated = useAuthStore(selectAuthHasHydrated);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const orders = useFirstOrderQuery(enabled, ASSISTANT_STALE_TIME);
  if (!hydrated) return 'unknown';
  if (!isAuthenticated) return 'guest';
  if (!orders.isSuccess) return 'unknown';
  return orders.data.length === 0 ? 'eligible' : 'already-ordered';
}
